import store, { RootState } from "@/redux/store";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import CustomPopover from "./CustomPopover";
import { getSourceMapForQid } from "../ReferenceWrapper/services/getSourceRefs";
import { convertSectionAnswersToHtml } from "../FpAppV4/services/convertSectionAnswersToHtml";
import {
  QueryAnswerType,
  SectionAnswerType,
} from "../FpAppV4/utils/fpAppV4Interface";
import TipTapViewer from "../FpFundBrain/QueryGrid/TipTapViewer";
import { ChevronDown, X } from "lucide-react";

const SectionAnswerViewer = ({
  sectionAnswer,
  htmlContent,
  index,
}: {
  sectionAnswer: SectionAnswerType;
  htmlContent: string;
  index: number;
}) => {
  const [show, setShow] = useState<boolean>(true);
  return (
    <div className="w-full bg-white text-[14px] p-4">
      <div
        className="flex items-center justify-between p-2 cursor-pointer"
        onClick={() => setShow(!show)}
      >
        <span className="w-[700px]  line-clamp-2">
          Section {index + 1} : {sectionAnswer?.sectionTitle}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-all duration-300 ${show ? "rotate-180" : ""}`}
        />
      </div>
      {show && (
        <div className="">
          <div className="h-fit overflow-y-auto ">
            <div className="relative">
              <TipTapViewer htmlContent={htmlContent} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ShowWorkPopover = ({
  anchorOrigin,
}: {
  anchorOrigin?: {
    vertical: "bottom" | "top";
    horizontal: "left" | "right";
  };
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );
  const [event, setEvent] = useState<MouseEvent | null>(null);
  const [leftId, setLeftId] = useState<string>("");
  const [details, setDetails] = useState<{ qid: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sectionAnswers, setSectionAnswers] = useState<SectionAnswerType[]>([]);
  const [htmlContents, setHtmlContents] = useState<string[]>([]);
  const queryAnswers = useSelector(
    (state: RootState) => state.fpAppV4Data.queryAnswers,
    shallowEqual
  );

  const getHtmlContent = async (qid: string) => {
    // Backfill queryAnswers in Redux first (fetches from API if missing)
    await getSourceMapForQid(qid);

    // Read from store after backfill, not the stale selector value
    const freshQueryAnswers = store.getState().fpAppV4Data.queryAnswers;
    const queryAnswer = freshQueryAnswers.find(
      (qa: QueryAnswerType) => qa.qid === qid
    ) as QueryAnswerType | undefined;
    if (!queryAnswer?.qid) return;
    if (queryAnswer.status === "failed") return;

    setSectionAnswers(
      queryAnswer?.toolResponse?.answer?.sectionResponses || []
    );

    const contents = await convertSectionAnswersToHtml(queryAnswer?.qid);

    setHtmlContents(contents || []);
    setIsLoading(false);
    return;
  };

  const openShowWorkPopover = async (
    e: MouseEvent,
    details: { qid: string }
  ) => {
    setAnchorEl(e.target as HTMLElement);
    setEvent(e);
    setLeftId(
      (
        (e.target as HTMLElement).getBoundingClientRect().left +
        Math.random() * 100
      ).toString()
    );
    setDetails(details);
    getHtmlContent(details.qid);
  };

  useEffect(() => {
    (window as Window).openShowWorkPopover = openShowWorkPopover;
  }, [openShowWorkPopover]);

  useEffect(() => {
    const updateLeftId = () => {
      if (event?.target)
        setLeftId(
          (
            (event?.target as HTMLElement).getBoundingClientRect().left +
            Math.random() * 100
          ).toString()
        );
    };
    // The file viewer's open/close toggle can shift this element's layout;
    // wait a frame past the browser's own paint so the read reflects the
    // settled layout rather than the one mid-transition.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(updateLeftId);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // can add more stuff in dependency array, which can affect the popover position
  }, [fileViewerData.isOpen]);

  return (
    <>
      <CustomPopover
        id={anchorEl ? "simple-popover-" + leftId : undefined}
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: anchorOrigin?.vertical || "bottom",
          horizontal: anchorOrigin?.horizontal || "left",
        }}
        closeOnClickOutside={false}
        stickyPosition={{
          top: Math.max(0, (window.innerHeight - 600) / 2),
          left: Math.max(0, (window.innerWidth - 800) / 2),
        }}
      >
        <div className="w-[800px] h-[600px] overflow-y-auto rounded-md">
          <div className="w-full h-[40px] bg-gray-100 text-black flex items-center justify-between sticky top-0 z-[1000]">
            <span className="ml-2">Answer Sections</span>
            <span
              className="p-2 cursor-pointer rounded-md hover:bg-gray-800 transition-all"
              onClick={() => setAnchorEl(null)}
            >
              <X className="w-4 h-4" />
            </span>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-full p-4">
              Loading ...
            </div>
          ) : (
            htmlContents.map((htmlContent, index) => (
              <div key={index}>
                <SectionAnswerViewer
                  sectionAnswer={sectionAnswers[index]}
                  htmlContent={htmlContent}
                  index={index}
                />
              </div>
            ))
          )}
        </div>
      </CustomPopover>
    </>
  );
};

export default ShowWorkPopover;
